import {
  Anchor,
  Badge,
  Box,
  Center,
  createStyles,
  Group,
  MantineColor,
  Progress,
  Stack,
  Text,
  ThemeIcon,
  UnstyledButton,
} from '@mantine/core';
import React, {ReactNode, useEffect, useState} from 'react';

import {
  AnalysisProgressStep,
  getAnalysisProgress,
  getAnalysisProgressPercent,
} from 'analysis-tool/progress/getAnalysisProgress';
import {
  GetAnalysisTemplate,
  GetAnalysisTemplate_analysisTemplate_AnalysisTemplateNode as AnalysisTemplateNode,
  GetAnalysisTemplateVariables,
} from 'analysis-tool/templates/graphql/__generated/GetAnalysisTemplate';
import {GET_ANALYSIS_TEMPLATE_QUERY} from 'analysis-tool/templates/graphql/GetAnalysisTemplate';
import shouldShowVariableInForm from 'analysis-tool/templates/shouldShowVariableInForm';
import {getLocationOrigin} from 'BenchlingUtil';
import {PipelineStepStatusCode, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery, {
  CachedDataWhenRefetchingNamedOption,
  QueryResultType,
  useBenchlingQuery,
} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import SelectableNotePart from 'notebook/mediocre/selection/block/SelectableNotePart';
import {AnalysisTemplateGroup} from 'notebook/notes/groups/analysis-template/AnalysisTemplateGroupConstants';
import getAnalysisStatusFromSteps from 'notebook/notes/groups/analysis-template/getAnalysisStatusFromSteps';
import getCorrespondingAnalysisSteps from 'notebook/notes/groups/analysis-template/getCorrespondingAnalysisSteps';
import getEmbeddedAnalysisInputTables, {
  EmbeddedAnalysisInputTable,
} from 'notebook/notes/groups/analysis-template/getEmbeddedAnalysisInputTables';
import getEmbeddedAnalysisInputValues from 'notebook/notes/groups/analysis-template/getEmbeddedAnalysisInputValues';
import {
  GetEmbeddedAnalysis,
  GetEmbeddedAnalysisVariables,
} from 'notebook/notes/groups/analysis-template/graphql/__generated/GetEmbeddedAnalysis';
import {
  GetEmbeddedAnalysisInputDatasets,
  GetEmbeddedAnalysisInputDatasetsVariables,
} from 'notebook/notes/groups/analysis-template/graphql/__generated/GetEmbeddedAnalysisInputDatasets';
import {GET_EMBEDDED_ANALYSIS_QUERY} from 'notebook/notes/groups/analysis-template/graphql/GetEmbeddedAnalysis';
import {GET_EMBEDDED_ANALYSIS_INPUT_DATASETS_QUERY} from 'notebook/notes/groups/analysis-template/graphql/GetEmbeddedAnalysisInputDatasets';
import hasAnalysisFinished from 'notebook/notes/groups/analysis-template/hasAnalysisFinished';
import groupKindToIcon from 'notebook/notes/groups/util/groupKindToIcon';
import {NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import {generateNoteLinkedObjectCollapseId} from 'notebook/notes/util/generateNLCollapseId';
import {useNotebookItemCollapse} from 'notebook/tables/collapse/useNotebookItemCollapse';
import useStepTypeInfos from 'pipelines/hooks/useStepTypeInfosQuery';
import {PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';
import {AssayRunField} from 'runs/assay-group/AssayRunFields';
import {AnalysisInputs} from 'runs/assay-group/ViewAnalysisInputsModal';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {ChipSize} from 'ui-library/chips/Chip';
import DatasetChip from 'ui-library/chips/DatasetChip';
import CollapserChevron from 'ui-library/CollapserChevron';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {JSONValue} from 'util/Types';

const useStyles = createStyles((theme) => ({
  groupWrapper: {
    backgroundColor: theme.white,
    display: 'inline-block',
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.md,
    maxWidth: '100%',
    position: 'relative',
    width: '100%',
  },
  nameBox: {
    backgroundColor: theme.colors.gray[0],
    border: `1px solid ${theme.colors.gray[2]}`,
    borderRadius: theme.radius.sm,
    minHeight: 36,
    minWidth: 0,
  },
  body: {
    borderTop: `1px solid ${theme.colors.gray[2]}`,
  },
}));

const useProgressStyles = createStyles((theme) => ({
  progressBar: {
    backgroundColor: theme.colors.blue[2],
  },
}));

const ANALYSIS_STATUS_BADGE: Record<PipelineStepStatusCode, {label: string; color: MantineColor}> = {
  [PipelineStepStatusCode.AI_PROPOSED]: {label: 'AI proposed', color: 'purple'},
  [PipelineStepStatusCode.BLOCKED]: {label: 'Blocked', color: 'gray'},
  [PipelineStepStatusCode.FAILED]: {label: 'Failed', color: 'red'},
  [PipelineStepStatusCode.HALTED]: {label: 'Halted', color: 'yellow'},
  [PipelineStepStatusCode.RUNNING]: {label: 'Running', color: 'blue'},
  [PipelineStepStatusCode.SUCCEEDED]: {label: 'Succeeded', color: 'green'},
  [PipelineStepStatusCode.SUCCEEDED_WITH_ERRORS]: {label: 'Succeeded with errors', color: 'yellow'},
  [PipelineStepStatusCode.WAITING]: {label: 'Waiting', color: 'yellow'},
};

function AnalysisStatusBadge({statusCode}: {statusCode: PipelineStepStatusCode}): JSX.Element {
  const {label, color} = ANALYSIS_STATUS_BADGE[statusCode];
  return (
    <Badge color={color} size="sm" variant="light" radius="sm">
      {label}
    </Badge>
  );
}

const ANALYSIS_POLL_INTERVAL_MS = 2000;

/** The height of the progress bar, a little less than the header's name box. */
const PROGRESS_BAR_HEIGHT = 28;

function AnalysisHeaderStatus({
  steps,
}: {
  steps: ReadonlyArray<AnalysisProgressStep & Parameters<typeof hasAnalysisFinished>[0][number]>;
}): JSX.Element {
  const {classes} = useProgressStyles();
  if (hasAnalysisFinished(steps)) {
    return <AnalysisStatusBadge statusCode={getAnalysisStatusFromSteps(steps)} />;
  }
  const progress = getAnalysisProgress(steps);
  const percent = getAnalysisProgressPercent(progress);
  const percentLabel = `${Math.round(percent)}%`;
  return (
    <Box pos="relative" w={200}>
      <Progress
        value={percent}
        size={PROGRESS_BAR_HEIGHT}
        radius="sm"
        classNames={{bar: classes.progressBar}}
        aria-label="Analysis progress"
      />
      <Center pos="absolute" top={0} right={0} bottom={0} left={0}>
        <Text size="xs" weight={FONT_WEIGHT_PROXIMA_SEMIBOLD}>
          {progress.isSendingData ? `Sending data... ${percentLabel}` : percentLabel}
        </Text>
      </Center>
    </Box>
  );
}

function Section({title, children}: {title: string; children: ReactNode}): JSX.Element {
  const [isExpanded, setIsExpanded] = useState(true);
  return (
    <Stack spacing="sm">
      <UnstyledButton
        aria-expanded={isExpanded}
        onClick={() => {
          setIsExpanded(!isExpanded);
        }}
      >
        <Group spacing="xs">
          <Text size="sm" weight={FONT_WEIGHT_PROXIMA_SEMIBOLD}>
            {title}
          </Text>
          <BenchlingIcon icon={isExpanded ? 'chevron-down' : 'chevron-right'} />
        </Group>
      </UnstyledButton>
      {isExpanded && children}
    </Stack>
  );
}

interface BlockLayoutProps {
  group: AnalysisTemplateGroup;
  editorProps: NoteEditorProps;
  indentation: number;
  /** Shown in the header's name box; defaults to the group's name. */
  title?: ReactNode;
  status: ReactNode;
  children: ReactNode;
}

function BlockLayout({
  group,
  editorProps,
  indentation,
  title,
  status,
  children,
}: BlockLayoutProps): JSX.Element {
  const {classes} = useStyles();
  const {isCollapsed, onToggleCollapsed} = useNotebookItemCollapse(
    generateNoteLinkedObjectCollapseId(editorProps.fileId, group.provenanceUuid)
  );
  const isExpanded = !isCollapsed || editorProps.viewer;

  return (
    <SelectableNotePart indentation={indentation}>
      <div
        className={classes.groupWrapper}
        {...getTestProps({component: 'AnalysisTemplateGroupView', element: 'groupWrapper'})}
      >
        <Group spacing="sm" noWrap={true} p="md">
          <ThemeIcon size="lg" radius="sm" variant="light" color="blue">
            <BenchlingIcon icon={groupKindToIcon(group.kind)} />
          </ThemeIcon>
          <Group spacing="sm" noWrap={true} px="sm" className={classes.nameBox}>
            {title ?? (
              <Text size="sm" weight={FONT_WEIGHT_PROXIMA_SEMIBOLD} truncate={true}>
                {group.name}
              </Text>
            )}
            <Badge color="blue" size="sm" variant="light" radius="sm" tt="none">
              Automation template
            </Badge>
          </Group>
          <Group ml="auto" spacing="sm" noWrap={true}>
            {status}
            {!editorProps.viewer && (
              <CollapserChevron
                isExpanded={!isCollapsed}
                name="analysis-template-group"
                onToggleCollapsed={onToggleCollapsed}
              />
            )}
          </Group>
        </Group>
        {isExpanded && (
          <Stack p="md" className={classes.body}>
            {children}
          </Stack>
        )}
      </div>
    </SelectableNotePart>
  );
}

function getAnalysisTableUrl(analysisUrl: string, dataFrameId: string): string {
  const url = new URL(analysisUrl, getLocationOrigin());
  url.searchParams.set('screen', 'view');
  url.searchParams.set('dataFrameId', dataFrameId);
  return url.toString();
}

/** The dataset of each input table, or a link to the table in the analysis when it isn't a dataset. */
function EmbeddedAnalysisInputTables({
  analysisUrl,
  inputTables,
}: {
  analysisUrl: string;
  inputTables: ReadonlyArray<EmbeddedAnalysisInputTable>;
}): JSX.Element {
  return (
    <BenchlingQuery<GetEmbeddedAnalysisInputDatasets, GetEmbeddedAnalysisInputDatasetsVariables>
      query={GET_EMBEDDED_ANALYSIS_INPUT_DATASETS_QUERY}
      variables={{dataFrameIds: inputTables.map(({dataFrameId}) => dataFrameId)}}
      renderLoading={() => <CenteredHorizontalThrobber />}
    >
      {({dataFrames}) => {
        const datasetIdByDataFrameId = new Map(
          dataFrames.flatMap((dataFrame) =>
            dataFrame.__typename === 'DataFrameNode' && dataFrame.dataset != null
              ? [[dataFrame.id, dataFrame.dataset.id] as const]
              : []
          )
        );
        return (
          <Group spacing="xl" align="flex-start">
            {inputTables.map(({dataFrameId, name, datasetId: sourceDatasetId}) => {
              const datasetId = sourceDatasetId ?? datasetIdByDataFrameId.get(dataFrameId);
              return (
                <div key={dataFrameId}>
                  <AssayRunField key={dataFrameId} title={name} isRequired={false}>
                    {datasetId != null ? (
                      <DatasetChip id={datasetId} size={ChipSize.SMALL} />
                    ) : (
                      <Anchor
                        href={getAnalysisTableUrl(analysisUrl, dataFrameId)}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="sm"
                      >
                        View in analysis
                      </Anchor>
                    )}
                  </AssayRunField>
                </div>
              );
            })}
          </Group>
        );
      }}
    </BenchlingQuery>
  );
}

function StepInputs({title, children}: {title: string; children: ReactNode}): JSX.Element {
  return (
    <Stack spacing="xs">
      <Text size="sm" weight={FONT_WEIGHT_PROXIMA_SEMIBOLD} color="dimmed">
        {title}
      </Text>
      {children}
    </Stack>
  );
}

interface AnalysisStepConfig {
  id: string;
  stepType: string;
  config: JSONValue;
}

interface StepInputGroup {
  stepId: string;
  stepType: PipelineStepType;
  inputTables: ReadonlyArray<EmbeddedAnalysisInputTable>;
  configVariables: AnalysisTemplateNode['configVariables'];
}

function EmbeddedAnalysisInputs({
  analysisTemplateId,
  analysisUrl,
  analysisSteps,
  inputTables,
}: {
  analysisTemplateId: string;
  analysisUrl: string;
  analysisSteps: ReadonlyArray<AnalysisStepConfig>;
  inputTables: ReadonlyArray<EmbeddedAnalysisInputTable>;
}): JSX.Element {
  const stepTypeInfos = useStepTypeInfos();
  const getStepTitle = (stepType: PipelineStepType): string =>
    stepTypeInfos.get(stepType)?.userFacingName ?? stepType;

  return (
    <BenchlingQuery<GetAnalysisTemplate, GetAnalysisTemplateVariables>
      query={GET_ANALYSIS_TEMPLATE_QUERY}
      variables={{analysisTemplateId}}
      renderLoading={() => <CenteredHorizontalThrobber />}
    >
      {({analysisTemplate}) => {
        if (analysisTemplate == null || isInaccessible(analysisTemplate)) {
          return <Text color="dimmed">The inputs of this analysis are not available.</Text>;
        }
        const {steps} = analysisTemplate;
        // Inputs the system binds, such as where outputs are sent, are left out, as when applying a template
        const configVariables = analysisTemplate.configVariables.filter((variable) =>
          shouldShowVariableInForm({
            configVariableKey: variable.configVariableKey,
            variableTypeConfigSpec: variable.variableTypeConfigSpec as PipelineConfigJsonSpec,
          })
        );
        if (inputTables.length === 0 && configVariables.length === 0) {
          return <Text color="dimmed">No inputs</Text>;
        }
        const variableBindings = getEmbeddedAnalysisInputValues(configVariables, steps, analysisSteps);
        // Inputs are grouped by their step, in the order of the template's steps. Input tables from analysis
        // steps that don't correspond to a template step come last.
        const correspondingSteps = getCorrespondingAnalysisSteps(steps, analysisSteps);
        const templateStepGroups: Array<StepInputGroup> = steps.map((step) => ({
          stepId: step.id,
          stepType: step.stepType,
          inputTables: inputTables.filter((table) => table.stepId === correspondingSteps.get(step.id)?.id),
          configVariables: configVariables.filter(
            ({pipelineStepDomain}) => pipelineStepDomain.id === step.id
          ),
        }));
        const groupedTableStepIds = new Set(
          templateStepGroups.flatMap((group) => group.inputTables.map(({stepId}) => stepId))
        );
        const otherTableGroups: Array<StepInputGroup> = [
          ...new Set(
            inputTables.map(({stepId}) => stepId).filter((stepId) => !groupedTableStepIds.has(stepId))
          ),
        ].map((stepId) => {
          const stepTables = inputTables.filter((table) => table.stepId === stepId);
          return {stepId, stepType: stepTables[0].stepType, inputTables: stepTables, configVariables: []};
        });
        return (
          <Stack spacing="lg">
            {[...templateStepGroups, ...otherTableGroups]
              .filter((group) => group.inputTables.length > 0 || group.configVariables.length > 0)
              .map((group) => (
                <StepInputs key={group.stepId} title={getStepTitle(group.stepType)}>
                  {group.inputTables.length > 0 && (
                    <EmbeddedAnalysisInputTables analysisUrl={analysisUrl} inputTables={group.inputTables} />
                  )}
                  {group.configVariables.length > 0 && (
                    <AnalysisInputs
                      // Some read-only fields only read their value when they mount, so they're remounted
                      // when polling brings in new values
                      key={JSON.stringify(group.configVariables.map(({id}) => variableBindings[id] ?? null))}
                      variableBindings={variableBindings}
                      analysisTemplate={{configVariables: group.configVariables}}
                      warnOnMissingRequiredValues={false}
                    />
                  )}
                </StepInputs>
              ))}
          </Stack>
        );
      }}
    </BenchlingQuery>
  );
}

function EmbeddedAnalysisBlock({
  analysisId,
  analysisTemplateId,
  ...layoutProps
}: Omit<BlockLayoutProps, 'status' | 'children'> & {
  analysisId: string;
  analysisTemplateId: string | null;
}): JSX.Element {
  const result = useBenchlingQuery<GetEmbeddedAnalysis, GetEmbeddedAnalysisVariables>(
    GET_EMBEDDED_ANALYSIS_QUERY,
    {
      variables: {analysisId},
      fetchPolicy: 'cache-and-network',
      renderCachedDataWhenRefetching: CachedDataWhenRefetchingNamedOption.WHEN_LOADING,
    }
  );
  const analysis = result.type === QueryResultType.SUCCESS ? result.data.analysis : null;
  const accessibleAnalysis = analysis == null || isInaccessible(analysis) ? null : analysis;
  // The embedding entry's write applies the template after commit, so the analysis can be read before it
  // has steps. Poll the analysis itself, rather than its modifiedAt, until it has finished: its status can
  // change without a newer modifiedAt.
  const hasFinished =
    analysis != null && (accessibleAnalysis == null || hasAnalysisFinished(accessibleAnalysis.steps));
  const {startPolling, stopPolling} = result;
  useEffect(() => {
    if (hasFinished) {
      stopPolling();
    } else {
      startPolling(ANALYSIS_POLL_INTERVAL_MS);
    }
    return stopPolling;
  }, [hasFinished, startPolling, stopPolling]);

  let inputs: ReactNode;
  if (analysisTemplateId == null) {
    inputs = <Text color="dimmed">This analysis template is no longer available.</Text>;
  } else if (result.type === QueryResultType.ERROR) {
    inputs = <Text color="dimmed">This analysis could not be loaded.</Text>;
  } else if (analysis == null) {
    inputs = <CenteredHorizontalThrobber />;
  } else if (accessibleAnalysis == null) {
    inputs = <Text color="dimmed">The inputs of this analysis are not available.</Text>;
  } else {
    inputs = (
      <EmbeddedAnalysisInputs
        analysisTemplateId={analysisTemplateId}
        analysisUrl={accessibleAnalysis.url}
        analysisSteps={accessibleAnalysis.steps}
        inputTables={getEmbeddedAnalysisInputTables(accessibleAnalysis.steps)}
      />
    );
  }

  return (
    <BlockLayout
      {...layoutProps}
      title={
        accessibleAnalysis && (
          <Anchor
            href={accessibleAnalysis.url}
            target="_blank"
            rel="noopener noreferrer"
            size="sm"
            weight={FONT_WEIGHT_PROXIMA_SEMIBOLD}
            truncate={true}
          >
            {accessibleAnalysis.name}
          </Anchor>
        )
      }
      status={accessibleAnalysis && <AnalysisHeaderStatus steps={accessibleAnalysis.steps} />}
    >
      <Section title="Inputs">{inputs}</Section>
      {/*
        TODO(BNCH-171937): Add an Outputs section showing what the analysis's Send to document steps sent. Their target
        is bound to the embedding entry, so today they add sections to the entry as when the template is
        applied in the analysis tool. Planned: record an embed association (analysis, block, input values)
        when the embed is created; the step then writes into the block's contained note parts instead of
        new entry sections, and this section renders them.
      */}
    </BlockLayout>
  );
}

export interface AnalysisTemplateGroupViewProps {
  notePartCid: string;
  indentation: number;
  group: AnalysisTemplateGroup;
  editorProps: NoteEditorProps;
}

/**
 * Read-only embed of an analysis in a notebook entry, laid out as the automation run block: the template
 * name, linked to the analysis, and the analysis's pipeline status in the header, then the values of the
 * template's inputs. The block embeds exactly one analysis, so its status is the block's status, and
 * while the analysis runs the header also shows how many of its steps have completed. The analysis is
 * polled by `analysisId` until it finishes, and the input values are read from its steps. When the reference has been
 * cleared (clone/cross-entry paste scrubs it), only the header renders.
 */
export function AnalysisTemplateGroupView({
  group,
  editorProps,
  indentation,
}: AnalysisTemplateGroupViewProps): JSX.Element {
  const {analysisId, analysisTemplateId} = group.groupMetadata;
  if (analysisId == null) {
    return (
      <BlockLayout group={group} editorProps={editorProps} indentation={indentation} status={null}>
        <Text color="dimmed">This analysis reference is no longer available.</Text>
      </BlockLayout>
    );
  }
  return (
    <EmbeddedAnalysisBlock
      group={group}
      editorProps={editorProps}
      indentation={indentation}
      analysisId={analysisId}
      analysisTemplateId={analysisTemplateId}
    />
  );
}

export default AnalysisTemplateGroupView;
